/**
 * Allow-list HTML sanitiser for the few places that have to render
 * metadata- or user-authored markup through `v-html`.
 *
 * The markup is parsed into an inert document with `DOMParser`, so nothing in
 * the input runs or fetches while it is being cleaned; everything outside the
 * allow-list for the requested profile is then dropped before the result is
 * handed back for rendering.
 *
 * `DOMParser` only exists in the browser, so this returns an empty string on
 * the server. Call sites that can render during SSR wrap the output in
 * `<client-only>`; the menu/icon call sites already load their content
 * client-side only.
 */

export type SanitizeProfile = "richText" | "icon";

/** Attributes accepted on any allowed element. */
const globalAttributes = ["class", "dir", "title", "role", "aria-hidden"];

/** Attributes accepted on any allowed SVG element. */
const svgAttributes = [
  "clip-rule",
  "cx",
  "cy",
  "d",
  "fill",
  "fill-opacity",
  "fill-rule",
  "focusable",
  "height",
  "offset",
  "opacity",
  "points",
  "preserveaspectratio",
  "r",
  "rx",
  "ry",
  "stop-color",
  "stop-opacity",
  "stroke",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-miterlimit",
  "stroke-opacity",
  "stroke-width",
  "transform",
  "viewbox",
  "width",
  "x",
  "x1",
  "x2",
  "xmlns",
  "y",
  "y1",
  "y2",
];

/**
 * Allowed tags per profile, mapped to the extra attributes each tag accepts on
 * top of {@link globalAttributes} (and {@link svgAttributes} inside an `<svg>`).
 */
const allowedTags: Record<SanitizeProfile, { [tag: string]: string[] }> = {
  richText: {
    a: ["href", "target", "rel"],
    b: [],
    blockquote: [],
    br: [],
    code: [],
    div: [],
    em: [],
    h1: [],
    h2: [],
    h3: [],
    h4: [],
    h5: [],
    h6: [],
    hr: [],
    i: [],
    img: ["src", "alt", "width", "height"],
    li: ["data-list"],
    ol: [],
    p: [],
    pre: [],
    s: [],
    small: [],
    span: [],
    strong: [],
    sub: [],
    sup: [],
    table: [],
    tbody: [],
    td: ["colspan", "rowspan"],
    tfoot: [],
    th: ["colspan", "rowspan"],
    thead: [],
    tr: [],
    u: [],
  },
  icon: {
    circle: [],
    defs: [],
    desc: [],
    div: [],
    ellipse: [],
    g: [],
    i: [],
    line: [],
    lineargradient: ["gradientTransform", "gradientUnits"],
    path: [],
    polygon: [],
    polyline: [],
    radialgradient: ["gradientTransform", "gradientUnits"],
    rect: [],
    span: [],
    stop: [],
    svg: [],
    title: [],
  },
};

/**
 * Tags that are dropped together with their contents. Everything else outside
 * the profile allow-list is unwrapped instead, so plain markup that we simply
 * do not know about still keeps its text.
 */
const strippedTags = [
  "animate",
  "animatemotion",
  "animatetransform",
  "applet",
  "audio",
  "base",
  "button",
  "canvas",
  "embed",
  "form",
  "foreignobject",
  "frame",
  "frameset",
  "iframe",
  "input",
  "link",
  "math",
  "meta",
  "noscript",
  "object",
  "portal",
  "script",
  "select",
  "set",
  "slot",
  "source",
  "style",
  "template",
  "textarea",
  "track",
  "use",
  "video",
];

/** CSS properties kept when filtering a `style` attribute. */
const allowedStyleProperties = [
  "background-color",
  "color",
  "direction",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "line-height",
  "padding-left",
  "text-align",
  "text-decoration",
];

/** Value fragments that can pull in or execute external content. */
const forbiddenValuePattern = /url\(|expression\(|javascript:|@import|\\/i;

function isSafeUrl(value: string, allowDataImage: boolean): boolean {
  // Strip whitespace and control characters first: an embedded tab makes
  // `java<tab>script:` a perfectly valid URL as far as the browser is concerned.
  const url = value.replace(/[\u0000-\u0020\u007F]/g, "").toLowerCase();

  if (allowDataImage && /^data:image\/(png|jpe?g|gif|webp|bmp);base64,/.test(url))
    return true;

  const scheme = url.match(/^([a-z][a-z0-9+.-]*):/);
  if (!scheme) return true; // relative, fragment or protocol-less path

  return ["http:", "https:", "mailto:", "tel:"].includes(scheme[1] + ":");
}

function sanitizeStyle(style: string): string {
  return style
    .split(";")
    .map((declaration) => {
      const separator = declaration.indexOf(":");
      if (separator < 0) return "";

      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();

      if (!allowedStyleProperties.includes(property)) return "";
      if (!value || forbiddenValuePattern.test(value)) return "";

      return `${property}: ${value}`;
    })
    .filter((declaration) => declaration != "")
    .join("; ");
}

function sanitizeAttributes(
  element: Element,
  allowedForTag: string[],
  insideSvg: boolean
): void {
  const allowed = insideSvg
    ? [...globalAttributes, ...svgAttributes, ...allowedForTag]
    : [...globalAttributes, "style", ...allowedForTag];
  const allowedLower = allowed.map((attribute) => attribute.toLowerCase());

  // Snapshot: removing an attribute mutates the live NamedNodeMap.
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();

    // Namespaced attributes (xlink:href and friends) are never in the
    // allow-list, and neither is any event handler.
    if (!allowedLower.includes(name) || name.startsWith("on")) {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (name == "style") {
      const style = sanitizeStyle(attribute.value);
      if (style == "") element.removeAttribute(attribute.name);
      else element.setAttribute(attribute.name, style);
      continue;
    }

    if (name == "href" || name == "src") {
      if (!isSafeUrl(attribute.value, name == "src"))
        element.removeAttribute(attribute.name);
      continue;
    }
  }

  // Links that open a new tab must not hand the opener over with them.
  if (element.localName.toLowerCase() == "a" && element.hasAttribute("target"))
    element.setAttribute("rel", "noopener noreferrer");
}

/**
 * Returns `html` with everything outside the profile's allow-list removed.
 * Returns an empty string when there is no DOM to parse with (SSR).
 */
export function sanitizeHtml(
  html: string | null | undefined,
  profile: SanitizeProfile = "richText"
): string {
  if (!html) return "";
  if (typeof DOMParser == "undefined") return "";

  const tags = allowedTags[profile];
  const body = new DOMParser().parseFromString(html, "text/html").body;

  // Document order, so an unwrapped element's children are still visited.
  for (const element of Array.from(body.querySelectorAll("*"))) {
    // A previous iteration may have dropped this element with its parent.
    if (!body.contains(element)) continue;

    const tag = element.localName.toLowerCase();

    if (strippedTags.includes(tag)) {
      element.remove();
      continue;
    }

    // hasOwnProperty, not `in`: `<constructor>` and `<tostring>` are tag names
    // the parser accepts, and `in` would find them on Object.prototype.
    if (!Object.prototype.hasOwnProperty.call(tags, tag)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }

    sanitizeAttributes(element, tags[tag], element.closest("svg") != null);
  }

  return body.innerHTML;
}
